import type { HomeModule } from '@/graphql/generated/hooks';
import { ChartColumn, HeartHandshake, Package, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { getAuthState } from './auth';
import { HR_CHILD_MODULES, MODULE_KEYS } from './module-keys';

/**
 * The app's modules — the level above the sidebar.
 *
 * Not the same thing as `MODULE_KEYS`: those are permission modules
 * (`customers`, `stock`, …). These are the products a person switches between
 * in the sidebar header, each with its own navigation and its own first path
 * segment (`/crm/...`, `/reports/...`).
 *
 * A registry, so that adding a module is a change to this list and not to the
 * layout.
 */
export type AppModuleId = 'crm' | 'reports' | 'hr' | 'stock';

export interface AppModule {
  id: AppModuleId;
  /** Keys under `modules.*` in the `common` namespace. */
  nameKey: string;
  descriptionKey: string;
  icon: LucideIcon;
  /** First path segment, and where picking the module lands. */
  basePath: string;
  landingPath: string;
  /**
   * The permission module whose grant opens this app module, or a list of
   * which any one will do.
   */
  permissionModule?: string | readonly string[];
  /** The grant on `permissionModule` that opens it; `view` when not given. */
  permissionAction?: string;
  /**
   * Older paths this module opens on top of the one it owns. The stock pages
   * kept their `/crm/stock/...` addresses when the Stock module was added, so
   * bookmarks and emailed links still work.
   */
  claimedPaths?: readonly string[];
  /** Field agents keep working in the CRM; this module is head office's. */
  hiddenFromAgents?: boolean;
}

/**
 * Not `stock:view`: the CRM's own stock pages need that, and the call centre
 * holds it. The module is procurement's, ticked per role.
 */
export const STOCK_MODULE_ACTION = 'open_module';

export const APP_MODULES: AppModule[] = [
  {
    id: 'crm',
    nameKey: 'modules.crm.name',
    descriptionKey: 'modules.crm.description',
    icon: Users,
    basePath: '/crm',
    landingPath: '/crm/dashboard',
  },
  {
    id: 'reports',
    nameKey: 'modules.reports.name',
    descriptionKey: 'modules.reports.description',
    icon: ChartColumn,
    basePath: '/reports',
    landingPath: '/reports',
    permissionModule: MODULE_KEYS.REPORTS,
  },
  {
    // Any HR child the person can view opens HR (ADR-0061 §4). It lands on the
    // dashboard, which shows a tile only for the areas they can view.
    id: 'hr',
    nameKey: 'modules.hr.name',
    descriptionKey: 'modules.hr.description',
    icon: HeartHandshake,
    basePath: '/hr',
    landingPath: '/hr',
    permissionModule: HR_CHILD_MODULES,
  },
  {
    id: 'stock',
    nameKey: 'modules.stock.name',
    descriptionKey: 'modules.stock.description',
    icon: Package,
    basePath: '/stock',
    landingPath: '/stock',
    permissionModule: MODULE_KEYS.STOCK,
    permissionAction: STOCK_MODULE_ACTION,
    claimedPaths: ['/crm/stock'],
    hiddenFromAgents: true,
  },
];

export const DEFAULT_APP_MODULE: AppModuleId = 'crm';

export function getAppModule(id: AppModuleId): AppModule {
  return APP_MODULES.find(module => module.id === id) ?? APP_MODULES[0];
}

/**
 * The module a path belongs to, or null for the screens that belong to none —
 * Settings and Help are part of the shell and keep whichever module was last
 * open.
 */
export function appModuleForPath(pathname: string): AppModuleId | null {
  const under = (prefix: string) => pathname === prefix || pathname.startsWith(`${prefix}/`);
  // A claimed path sits inside another module's base path (`/crm/stock` is
  // under `/crm`), so claims are checked first.
  const claimed = APP_MODULES.find(module => module.claimedPaths?.some(under));
  const match = claimed ?? APP_MODULES.find(module => under(module.basePath));
  return match?.id ?? null;
}

const STORAGE_KEY = 'payg.lastAppModule';

/**
 * Kept per person. A browser is shared in a branch office, and the module an
 * administrator left open is not somewhere to land the agent who signs in next.
 */
function storageKey(): string {
  return `${STORAGE_KEY}:${getAuthState().user?.id ?? 'anonymous'}`;
}

/** The module last worked in, so signing in returns there. */
export function readLastAppModule(): AppModuleId {
  try {
    const stored = localStorage.getItem(storageKey());
    const known = APP_MODULES.find(module => module.id === stored);
    return known?.id ?? DEFAULT_APP_MODULE;
  } catch {
    return DEFAULT_APP_MODULE;
  }
}

const HOME_MODULES: Record<HomeModule, AppModuleId> = {
  CRM: 'crm',
  REPORTS: 'reports',
  HR: 'hr',
  STOCK: 'stock',
};

/** The switcher's module for a role's home module. */
export function appModuleForHome(homeModule: HomeModule): AppModuleId {
  return HOME_MODULES[homeModule];
}

/**
 * At sign-in, open on the module the person's roles name. With none named,
 * leave the one they last worked in. Call after the signed-in user is known:
 * the remembered module is kept per person.
 */
export function startInHomeModule(homeModule: HomeModule | null | undefined): void {
  if (homeModule) rememberAppModule(appModuleForHome(homeModule));
}

export function rememberAppModule(id: AppModuleId): void {
  try {
    localStorage.setItem(storageKey(), id);
  } catch {
    // Private windows can refuse storage; the default module is a fine fallback.
  }
}
